import {createElement} from 'react';

import classNames from 'classnames';

import type {ElementType, ReactNode, Ref, ComponentProps} from 'react';

export type GroupProps<T extends ElementType = 'div'> = {
    /**
     * Child nodes rendered inside the flex container. Wrap individual items in
     * `<Item>` to control per-item grow, shrink, basis, or alignment.
     */
    children?: ReactNode,
    /**
     * Polymorphic element type. Pass any valid HTML tag name or React component to
     * change the rendered element without losing native prop types.
     * Defaults to `"div"`.
     */
    as?: T,
    /**
     * CSS `display` value for the container. Use `"inline-flex"` when the group
     * should flow inline with surrounding text or other inline elements.
     * Defaults to `"flex"`.
     */
    display?: 'flex' | 'inline-flex',
    /**
     * Main axis direction. `"h"` sets `flex-direction: row`; `"v"` sets
     * `flex-direction: column`. When omitted the browser default (`row`) applies.
     */
    direction?: 'h' | 'v',
    /**
     * CSS `justify-content` — controls distribution of items along the main axis.
     * Use `"space-between"` for edge-to-edge spreading, `"center"` for centered
     * clusters, etc.
     */
    justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly' | 'start' | 'end' | 'left' | 'right' | 'normal',
    /**
     * CSS `align-items` — controls how items are aligned on the cross axis within
     * each line. `"stretch"` (browser default) makes all items fill the container
     * height; `"center"` vertically centers them in a row layout.
     */
    alignItems?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch' | 'start' | 'end' | 'self-start' | 'self-end' | 'normal',
    /**
     * CSS `align-content` — controls distribution of multiple lines when `wrap`
     * is enabled and there is extra space on the cross axis. Has no effect on
     * single-line containers.
     */
    alignContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly' | 'stretch' | 'start' | 'end' | 'baseline' | 'normal',
    /**
     * CSS `flex-wrap` value. `"wrap"` allows items to flow onto multiple lines
     * when they exceed the container width. Defaults to `"nowrap"`.
     */
    wrap?: 'nowrap' | 'wrap' | 'wrap-reverse',
    /**
     * Design-system gap token applied as the spacing between flex items. Maps to
     * CSS custom properties defined in the design tokens. Defaults to `"m"`.
     * Use `"none"` to remove all spacing.
     */
    gap?: 'none' | 'xxs' | 'xs' | 's' | 'sm' | 'm' | 'l' | 'xl' | 'xls' | 'xxl' | 'xxxl',

    /**
     * Ref to the underlying element.
     * @deprecated It will be removed in 6 months 09-13-2026. Use `ref` instead of `_ref` for better compatibility with React 19.
     */
    _ref?: Ref<T>,
} & ComponentProps<T>;

/**
 * Flexible layout primitive that renders a CSS Flexbox container with design-system
 * gap tokens and type-safe polymorphic `as` support.
 *
 * Wraps any element type (`as="ul"`, `as="nav"`, etc.; defaults to `"div"`) and
 * applies directional (`direction="h"` / `"v"`), alignment (`justify`, `alignItems`,
 * `alignContent`), wrapping (`wrap`), and spacing (`gap`) styles via CSS custom
 * properties. All other native element props are forwarded.
 *
 * Use `Item` (from the same package) as a direct child to control per-item flex
 * properties (grow, shrink, basis, padding).
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/layout-group--documentation}
 */
export function Group<T extends ElementType = 'div'>(props: GroupProps<T>) {
    const {
        as = 'div',
        gap = 'm',
        direction,
        display,
        justify,
        wrap,
        alignItems,
        alignContent,
        className,
        _ref,
        ...otherProps
    } = props;

    const classes = classNames('group', `group_${gap}`, {
        group_v: direction === 'v',
        group_h: direction === 'h',
    });

    const groupStyle = {
        '--gr__d': display,
        '--gr__j': justify,
        '--gr__ai': alignItems,
        '--gr__ac': alignContent,
        '--gr__w': wrap,
    };

    // Code template for group items customization
    // const children = Children.map(props.children, (child, index) => {
    //
    //     if (child && isValidElement(child)) {
    //         return child.type === Item
    //             ? Children.only(child)
    //             : <Item key={index}>{child}</Item>;
    //     }
    //     return child;
    // });

    return createElement(as, {
        ref: _ref,
        ...otherProps,
        style: {...groupStyle, ...otherProps.style},
        className: classNames(
            className,
            classes,
        ),
    }, props.children);
}
